/**
 * Level Manager - Handles session-scoped user level retrieval and caching
 *
 * This module provides efficient user level retrieval that happens once per session
 * and is reused throughout the app. It ensures data consistency across Firebase,
 * localStorage, and in-memory cache.
 *
 * @module levelManager
 */

import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebaseApp';
import { fetchLastPlacementTestResult } from './testResults';

/**
 * Validates that a level is within the 1-12 Quebec Francisation Scale
 * @param {*} level - The level value to validate
 * @returns {boolean} True if level is valid (1-12 integer)
 */
export const isValidLevel = (level) => {
  return Number.isInteger(level) && level >= 1 && level <= 12;
};

/**
 * Normalizes any level value to the 1-12 scale
 * Handles various input formats: numbers, strings, labels
 *
 * @param {*} level - The level value to normalize
 * @returns {number|null} Normalized level (1-12) or null if invalid/not set
 */
export const normalizeLevel = (level) => {
  // Handle null/undefined - return null to indicate "no level set"
  if (level === null || level === undefined) {
    return null;
  }

  // If it's already a valid number in range
  if (typeof level === 'number') {
    if (isValidLevel(level)) {
      return level;
    }
    // If number but out of range, try to use it anyway (for backward compatibility)
    const parsed = parseInt(level);
    if (isValidLevel(parsed)) {
      return parsed;
    }
  }

  // Handle string numbers
  if (typeof level === 'string') {
    const trimmed = level.trim();
    const parsed = parseInt(trimmed);
    if (!isNaN(parsed) && isValidLevel(parsed)) {
      return parsed;
    }

    // Map label-based levels
    const labelMap = {
      'Get_Started': 1,
      'Beginner': 3,
      'Intermediate': 6,
      'Advanced': 10,
    };

    if (labelMap.hasOwnProperty(trimmed)) {
      return labelMap[trimmed];
    }
  }

  // Invalid value - return null to indicate "no level set"
  console.warn(`Invalid level value provided: ${JSON.stringify(level)}. Returning null.`);
  return null;
};

/**
 * Fetches user level from Firestore
 * @param {string} userId - The user ID
 * @returns {Promise<number|null>} Level from Firestore or null
 */
async function fetchLevelFromFirestore(userId) {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const level = userDoc.data().level;
      // Normalize level first to handle strings, labels, etc.
      const normalized = normalizeLevel(level);
      if (normalized !== null) {
        console.log('Level retrieved from Firestore:', normalized, '(raw:', level + ')');
        return normalized;
      } else if (level !== undefined) {
        console.warn('Invalid level in Firestore:', level);
      }
    }
    return null;
  } catch (error) {
    console.warn('Error fetching level from Firestore:', error);
    return null;
  }
}

/**
 * Extracts level from latest placement test result
 * @param {string} userId - The user ID
 * @returns {Promise<number|null>} Level from latest test or null
 */
async function fetchLevelFromPlacementTest(userId) {
  try {
    const latestResult = await fetchLastPlacementTestResult();

    if (!latestResult || !latestResult.estimated_level) {
      return null;
    }

    // Extract score and map to 1-12 scale
    const score = latestResult.estimated_level.score_average;
    if (typeof score === 'number') {
      // Score is typically 0-100, map to 1-12 scale
      // 0-8 → 1, 9-16 → 2, ... 91-100 → 12
      const level = Math.min(12, Math.max(1, Math.ceil(score / 8.33)));
      if (isValidLevel(level)) {
        console.log('Level extracted from placement test:', level, '(score:', score + ')');
        return level;
      }
    }
    return null;
  } catch (error) {
    console.warn('Error fetching level from placement test:', error);
    return null;
  }
}

/**
 * Initializes user level for the current session
 * Always fetches fresh from Firestore, then updates localStorage cache
 *
 * Priority order:
 * 1. Firestore user document (source of truth - always fetched fresh)
 * 2. Latest placement test result (fallback)
 * 3. null (no level set - user should take placement test)
 *
 * localStorage is updated with the result for fast access during the session
 *
 * @param {string} userId - The user ID
 * @param {number|null} [prefetchedLevel=undefined] - Pre-fetched and normalized level from a combined Firestore read.
 *   When provided, skips the separate Firestore getDoc call.
 * @returns {Promise<number|null>} Normalized level (1-12 scale) or null if not set
 */
export const initializeUserLevel = async (userId, prefetchedLevel = undefined) => {
  if (!userId) {
    console.warn('User ID not provided to initializeUserLevel, returning null');
    localStorage.removeItem('userLevel');
    return null;
  }

  try {
    // Use pre-fetched level if provided (avoids duplicate Firestore read)
    const firestoreLevel = prefetchedLevel !== undefined ? prefetchedLevel : await fetchLevelFromFirestore(userId);
    if (firestoreLevel !== null) {
      localStorage.setItem('userLevel', firestoreLevel.toString());
      return firestoreLevel;
    }

    // Fallback to placement test
    const testLevel = await fetchLevelFromPlacementTest(userId);
    if (testLevel !== null) {
      localStorage.setItem('userLevel', testLevel.toString());
      return testLevel;
    }

    // No level found - return null to indicate user should take placement test
    console.log('No level found for user, returning null (user should take placement test)');
    localStorage.removeItem('userLevel');
    return null;
  } catch (error) {
    console.error('Error initializing user level:', error);
    localStorage.removeItem('userLevel');
    return null; // Return null on error
  }
};

/**
 * Updates user level after placement test completion
 * Writes to Firestore (source of truth)
 * localStorage will be refreshed on next session start
 *
 * @param {string} userId - The user ID
 * @param {number} newLevel - The new level (1-12)
 * @returns {Promise<boolean>} True if update successful
 */
export const updateUserLevelAfterTest = async (userId, newLevel) => {
  if (!userId) {
    console.error('User ID not provided to updateUserLevelAfterTest');
    return false;
  }

  // Validate new level
  if (!isValidLevel(newLevel)) {
    console.error('Invalid level provided to updateUserLevelAfterTest:', newLevel);
    return false;
  }

  try {
    // Update Firestore (source of truth)
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      level: newLevel,
      levelUpdatedAt: serverTimestamp()
    });
    console.log('Level updated in Firestore:', newLevel);
    return true;
  } catch (error) {
    console.error('Error updating user level in Firestore:', error);
    return false;
  }
};

/**
 * Session-level cache for efficient level retrieval
 * Prevents multiple database calls per session
 */
const sessionLevelCache = {
  level: null,
  userId: null,
  fetchedAt: null,
  isFetching: false
};

/**
 * Gets the cached user level if available
 * Returns null if cache is empty or invalidated
 *
 * @returns {number|null} Cached level or null if not initialized
 */
export const getCachedUserLevel = () => {
  return sessionLevelCache.fetchedAt ? sessionLevelCache.level : null;
};

/**
 * Clears cached level, forcing re-fetch on next initialization
 * Used after placement tests or manual level changes
 *
 * @returns {void}
 */
export const invalidateUserLevel = () => {
  console.log('User level cache invalidated');
  sessionLevelCache.level = null;
  sessionLevelCache.userId = null;
  sessionLevelCache.fetchedAt = null;
  sessionLevelCache.isFetching = false;
};

/**
 * Gets initialization status - useful for tracking level data freshness
 *
 * @returns {Object} Status object with cache information
 */
export const getLevelCacheStatus = () => {
  return {
    isCached: !!sessionLevelCache.fetchedAt,
    level: sessionLevelCache.level,
    userId: sessionLevelCache.userId,
    fetchedAt: sessionLevelCache.fetchedAt,
    isFetching: sessionLevelCache.isFetching,
    cacheAgeMs: sessionLevelCache.fetchedAt ? Date.now() - sessionLevelCache.fetchedAt : null
  };
};

/**
 * Aliases for backward compatibility with utils.js naming and test files
 * These allow gradual migration from utils.js to levelManager.js
 */
export const isValidQuebecLevel = isValidLevel;
export const levelToQuebecScale = normalizeLevel;
export const clearUserLevelCache = invalidateUserLevel;
export const invalidateUserLevelCache = invalidateUserLevel;

/**
 * Initialize user level with session caching
 * Prevents multiple database calls per session
 *
 * @param {string} userId - The user ID
 * @returns {Promise<number>} User level (1-12) from cache or newly fetched
 */
export const getOrInitializeUserLevel = async (userId) => {
  // Return cached value if already initialized for this user
  if (sessionLevelCache.fetchedAt && sessionLevelCache.userId === userId) {
    console.log(`Returning cached user level: ${sessionLevelCache.level}`);
    return sessionLevelCache.level;
  }

  // Prevent concurrent fetches
  if (sessionLevelCache.isFetching) {
    console.log('User level fetch already in progress, waiting...');
    while (sessionLevelCache.isFetching) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    return sessionLevelCache.level;
  }

  sessionLevelCache.isFetching = true;

  try {
    const level = await initializeUserLevel(userId);

    // Update session cache
    sessionLevelCache.level = level;
    sessionLevelCache.userId = userId;
    sessionLevelCache.fetchedAt = Date.now();
    sessionLevelCache.isFetching = false;

    console.log(`✅ User level cached: ${level}`);
    return level;
  } catch (error) {
    sessionLevelCache.isFetching = false;
    throw error;
  }
};
